import { createContext } from 'react';
import type { GroupRef } from '@api/types/groups';

export type OrgRole = 'AIBUILD_ADMIN' | 'ORG_ADMIN' | 'MEMBER';

/**
 * Free-form lead-qualification answers captured by the post-payment
 * onboarding form. Shape is owned by the frontend (see
 * `frontend/src/pages/Onboarding/options.ts`); the backend just persists
 * the JSON as-is and forwards it to the AI service.
 */
export interface UserProfileData {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  role?: string;
  industry?: string;
  companySize?: string;
  intents?: string[];
  intentsOther?: string;
  cadSoftware?: string[];
  cadOther?: string;
  caeTools?: string[];
  caeOther?: string;
  plm?: string[];
  plmOther?: string;
  camSoftware?: string[];
  camOther?: string;
  mfgSetup?: string[];
  mfgSetupOther?: string;
  mes?: string[];
  mesOther?: string;
}


export interface User {
  id: string;
  displayName?: string | null;
  email: string;
  token: string;
  refreshToken: string | null;
  orgRole?: OrgRole;
  billingStatus: 'PENDING' | 'ACTIVE' | 'SUSPENDED';
  pendingInviteToken?: string | null;
  eulaAccepted: boolean;
  eulaUrl?: string | null;
  mfaEnabled: boolean;
  mfaEnforced: boolean;
  isPersonalOrg: boolean;
  freeTierCredits: number;
  // Org identity for Datadog monitoring
  orgId?: string | null;
  orgName?: string | null;
  planType?: string | null;
  orgCreatedAt?: string | null;
  // User monitoring fields
  userCreatedAt?: string | null;
  lastLoginAt?: string | null;
  hasCamAccess?: boolean;
  userActivatedAt?: string | null;
  // Org usage metrics for Datadog monitoring
  orgUserCount?: number;
  orgCreditsPerMonth?: number | null;
  orgNextBillingDate?: string | null;
  // Onboarding (lead-qualification) profile. `hasCompletedOnboarding === false`
  // is the gate that redirects the user to /onboarding on every login.
  profileData?: UserProfileData | null;
  hasCompletedOnboarding?: boolean;
  onboardingSkipped?: boolean;
  // Active group memberships, surfaced as a Datadog RUM tag.
  groups?: GroupRef[];
  ssoProvider?: string | null;
}

export interface AuthContextType {
  user: User | null;
  signup: (email: string, password: string, displayName?: string, turnstileToken?: string) => Promise<{ status: 'created' | 'code_resent' }>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  completeMfa: (code: string) => Promise<void>;
  completeSsoLogin: (code: string, state: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  acceptEula: () => Promise<void>;
  updateMfaEnabled: (enabled: boolean) => void;
  updateBillingStatus: (status: 'PENDING' | 'ACTIVE' | 'SUSPENDED') => void;
  /** Update local user state after the onboarding form has been finished or skipped. */
  markOnboardingComplete: (data: UserProfileData | null, skipped: boolean) => void;
  clearPendingInvite: () => void;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  /** When set, the user must log in via Aibuild CAM first (unmigrated ai-sync account). */
  camLoginUrl: string | null;
  clearError: () => void;
  mfaRequired: boolean;
  mfaToken: string | null;
  mfaEmail: string | null;
  isCognitoMfa: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export default AuthContext;
